  <template>
  <div class="search-bar">
    <div class="logo" @click="$router.replace('/')">
      <img :src="$cvWebpFn(`https://img.kilimall.com/c/h5/logo_icon_164_x3.png`)" alt="">
    </div>

    <div class="search-input-box">
      <div class="input" id="pc__search-input">
        <van-field v-model="keyword" @focus="handleInputFocus" placeholder="I'm looking for..." />
        <div class="search-button" @click="handleSearchClick">
          <img :src="$cvWebpFn('https://img.kilimall.com/c/h5/imgs/search-icon@2x.png', { w: 80 })" alt="">
        </div>
      </div>
      <div class="search-hots">
        <!-- <span class="hot-label">New Arrivals:</span> -->
        <span class="hot-keys">
          <span
            v-for="(key, idx) in hotSearchKeys"
            :key="idx"
            class="hot-key"
          >
            <!-- 勿在 :href 里调用会写 url-cache 的函数：每次页面重渲染都会刷 set-params -->
            <a
              :href="getHotSearchHref(key)"
              target="_blank"
              @click="handleHotSearchNavigate(key, $event)"
            >{{ key.keyword }}</a>
          </span>
        </span>
      </div>

      <!-- 联想词 -->
      <div class="associative-words" v-show="suggestions.length > 0 && !isSuggestion">
        <p class="keyword" v-for="(key, idx) in suggestions" :key="idx" @click="handleSuggestionClick(key, searchTypeMap.relatedSearch)">{{ key }}</p>
      </div>
    </div>

    
    <div class="opt-btn cart">
      <SearchOperationContainer>
        <template #title>
          <div class="opt-btn-content" @click="$router.push('/shopping-cart')">
            <van-badge :content="useShopingCartTotal().value < 0 ? 0 : useShopingCartTotal().value" :show-zero="false">
              <img :src="$cvWebpFn('https://img.kilimall.com/c/h5/imgs/cart-icon@2x.png', { w: 80 })" alt="" class="opt-btn-icon">
            </van-badge>
            <span class="label">Cart</span>
            <van-icon name="play" size="14px" class="jt" />
          </div>
        </template>
        <div class="cart-list-body">
          <div class="shopping-title">The Latest Addition</div>
          <div class="shopping-list">
            <div
              v-for="(item, index) in useShoppingCartLaconicList().value"
              :key="index"
              class="cart-item"
              @click="toDetail(item)"
            >
              <img class="product-image" v-lazy="$cvWebpFn(item.productImage)" alt="">
              <p class="product-title">{{ item.spec }}</p>
              <div class="product-price">
                {{ $currencySymbol() }} {{ $toThousandsFn(item.salePrice) }}
              </div>
            </div>

            <div class="no-data" v-if="useShoppingCartLaconicList().value?.length <= 0">
              No Item In Your Cart
            </div>
          </div>

          <van-button @click="$router.push('/shopping-cart')" class="view-cart-btn" type="primary">View More</van-button>
        </div>
      </SearchOperationContainer>
    </div>

    <div class="opt-btn my">
      <SearchOperationContainer>
        <template #title>
          <div class="opt-btn-content" @click="handleMyCenterClick()">
            <van-badge :dot="useMessageNotReadTotal().value > 0 || isDot"  :show-zero="false">
              <img :src="$cvWebpFn('https://img.kilimall.com/c/h5/imgs/user-icon@2x.png', { w: 80 })" alt="" class="opt-btn-icon">
            </van-badge>
            <span class="label" :title="userName.source">
              {{ userName.nick }}
            </span>
            <van-icon name="play" size="14px" class="jt" />
          </div>
        </template>
        <div class="my-list-body">
          <template v-if="isLogin">
            <div
              v-for="(item, idx) in myCenterOpts"
              :key="idx"
              class="my-opt-item"
              @click="handleMyCenterClick(item)"
            >
              <van-icon
                v-if="item.path === 'live-chat'"
                :name="item.icon"
                :badge="useMessageNotReadTotal().value"
                :badge-props="{ showZero: false }"
                size="22px"
              />
              <van-icon :name="item.icon" size="22px" v-else></van-icon>
              <span class="my-opt-label">{{ item.label }}</span>
            </div>

            <van-button class="view-cart-btn" type="primary" @click="handleLoginOutClick">Logout</van-button>
          </template>
          <van-button v-else class="view-cart-btn" type="primary" @click="handleLoginClick">Login</van-button>
        </div>
      </SearchOperationContainer>
    </div>
  </div>
</template>

<script setup lang="ts">
import SearchOperationContainer from './SearchOperationContainer.vue';
import { getHomeHotKeywordsCache, getSuggestions, suggestionClick } from '@/api/models/system'
import { logoutResetUserIdFn } from '@/plugins/bi-sa.client'
import { buildStoreSearchListingSource, resolveStoreHomeFirstPage } from '@/plugins/utils'
import { parseSourceToParts, resolveCurrentPageCode } from '@/utils/traffic-source'

const props = defineProps<{
  source?: string,
  searchClick?: (keyword: string) => void
}>()
enum searchTypeMap {
  enterSearch = "enterSearch",
  historySearch = "historySearch",
  relatedSearch = "relatedSearch",
  hotSearch = "hotSearch"
}
let searchType: searchTypeMap = searchTypeMap.enterSearch
const { $debounce, $toListingDetail } = useNuxtApp()
const isLogin = computed(()=> {
  return !!useUserInfo().value?.accessToken
})

const userName = computed<{ nick: string, source: string }>(() => {
  let nick = useUserInfo().value?.nick || 'My Account'

  if(nick.length > 20) {
    nick = nick.slice(0, 18) + '...'
  }
  return { nick, source: useUserInfo().value?.nick}
})
const isDot = computed(() => {
  if (useUserInfo().value?.accessToken && (!useUserInfo().value?.email || !useUserInfo().value?.phone)
    && window && !localStorage.getItem('pc_user_info_dot')) {
    // 已登录， 并且手机号或邮箱存在未填写的情况， 并且未点击过红点区域， 显示红点
    return true
  }
  return false
})
const { $getHotKeysJumpUrl, $throttle } = useNuxtApp()
const queryParams = useRoute().query
const router = useRouter()
const route = useRoute()
const keyword = ref<string>((queryParams.q || queryParams.keyword) as string || '')
const suggestions = ref<string[]>([])
watch(
  () => keyword.value,
  $debounce(async function () {
    if(!isSuggestion.value) {
      const res = await getSuggestions(keyword.value)
      suggestions.value = res
    }
  }, 500)
)
onMounted(() => {
  document.getElementById('pc__search-input')?.addEventListener(
    'keydown',
    $throttle(function(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        if(!keyword.value) {
          showToast('Please enter keywords first.')
          return
        }
        
        handleSearchClick()
      }
    }, 200)
  )

  window.addEventListener('click', () => {
    suggestions.value = []
  })
})

interface ICenterOpt { label: string, path: string ,icon: string }
const myCenterOpts: ICenterOpt[] = [
  { label: 'Orders', path: '/orders', icon: 'orders-o' },
  { label: 'After Sale', path: '/afterSalePc', icon: 'refund-o' },
  { label: 'Address Book', path: '/address', icon: 'location-o' },
  { label: 'Favorites', path: '/wish-list', icon: 'like-o' },
  { label: 'Vouchers', path: '/voucher', icon: 'coupon-o' },
  { label: 'Message', path: '/message/station-letters', icon: 'volume-o' },
  { label: 'Chats with Sellers', path: 'live-chat', icon: 'chat-o' }
]

const toDetail = (item: IShoppingCartLaconic) => {
  const { listingId, skuId, productTitle, shortTitle } = item

  $toListingDetail(listingId, { skuId, title: productTitle, shortTitle })
}

/** 进入搜索结果页前的页面：当前路由；已在搜索结果页则保留 URL source 里的 first_page */
const resolveSearchFirstPage = () => {
  if (route.path.startsWith('/search') && !route.path.startsWith('/search-input')) {
    const [, , , firstPageFromSource] = parseSourceToParts(String(route.query.source || ''))
    if (firstPageFromSource) return firstPageFromSource
  }
  return resolveCurrentPageCode(route) || 'home'
}

/** 热搜 5 段 source；firstPage 与普通搜索一致，latestPage 由 getHotKeysJumpUrl 按 linkType 补全 */
const buildHotSearchSource = (kw: string) =>
  `searchProduct|hotSearch|${kw}|${resolveSearchFirstPage()}|`

/** 仅生成 href，不写 url-cache（模板渲染会多次调用） */
const getHotSearchHref = (key: IHotKeywords): string => {
  const { linkType, linkValue, keyword: _keyword } = key
  const kw = keyword.value || _keyword
  const res = $getHotKeysJumpUrl(
    { linkType, linkValue, keyword: kw },
    true,
    buildHotSearchSource(kw),
    { writeCache: false }
  )
  return res?.href || ''
}

/** listing/sku 热搜：点击时再写 cache，再新开页 */
const handleHotSearchNavigate = async (key: IHotKeywords, e: MouseEvent) => {
  const { linkType, linkValue, keyword: _keyword } = key
  if (![5, 10].includes(Number(linkType))) return

  e.preventDefault()
  // Safari：必须在 await 之前同步 open，否则会提示弹出式窗口被阻止
  const popup = window.open('about:blank', '_blank')
  const kw = keyword.value || _keyword
  const res = $getHotKeysJumpUrl(
    { linkType, linkValue, keyword: kw },
    true,
    buildHotSearchSource(kw),
    { writeCache: true }
  )
  if (res.cacheWrite) {
    await res.cacheWrite.catch(() => {})
  }
  if (!res.href) {
    popup?.close()
    return
  }
  if (popup && !popup.closed) {
    popup.location.replace(res.href)
  } else {
    window.location.href = res.href
  }
} 


const hotSearchKeys = ref<IHotKeywords[]>([])
getHomeHotKeywordsCache().then((r: any) => {
  // app原生页 和 品牌页 H5暂不支持
  hotSearchKeys.value = (r.hotKeywords || []).filter((c: IHotKeywords) => ![4, 7].includes(c.linkType))
})

const handleSearchClick = () => {
  if(props.searchClick) {
    props.searchClick(keyword.value)
  } else {
    const query: IAnyObject = {
      q: keyword.value,
      page: 1,
      // ...queryParams
    }

    if(!keyword.value) {
      showToast('Please enter keywords first.')
      return
    }
    const firstPage = resolveSearchFirstPage()
    let _source = `searchProduct|${searchType}|${keyword.value}|${firstPage}|searchProductResults`
    // 店铺搜索：first_page 取自店铺页 URL（cart / productDetail 等）
    if(queryParams.from === 'store') {
      _source = buildStoreSearchListingSource(keyword.value, route)
      const storeFirstPage = resolveStoreHomeFirstPage(route)
      if (storeFirstPage) {
        query.first_page = storeFirstPage
      }
    }

    query.source = _source

    const href = router.resolve({ path: '/search', query })
    location.replace(href.href)
    // router.push({ path: '/search', query })
  }
}

const isSuggestion = ref<boolean>(false)
const handleSuggestionClick = (key: string, type: searchTypeMap) => {
  isSuggestion.value = true
  keyword.value = key
  searchType = type

  handleSearchClick()

  // 联想词统计
  suggestionClick(key).catch((e: any) => {
    console.error(`联想词 ${key} 统计失败：`, e)
  })
}
const handleInputFocus = () => {
  isSuggestion.value = false
}

const handleLoginOutClick = () => {
  useCookie('UserInfo').value = ''
  useUserInfo().value = null
  logoutResetUserIdFn()
  window.location.href = '/login'
}
const handleLoginClick = () => {
  router.push(`/login?redirect=${encodeURI(route.fullPath)}`)
}

const handleMyCenterClick = (item?: ICenterOpt) => {
  if(!useUserInfo().value?.accessToken) {
    router.push(`/login?redirect=${encodeURI(route.fullPath)}`)
    return
  }
  if(!item) {
    // 点击了红点区域， 记录已点击
    if (isDot.value) {
      localStorage.setItem('pc_user_info_dot', '1')
    }
    router.push('/orders')
  } else {
    if(item.path === 'live-chat') {
      if(!useUserInfo().value.accessToken) {
        handleLoginClick()
      } else {
        usePCLiveChatPopupShow().value = true
      }
    } else {
      router.push(item.path)
    }
  }
}
</script>

<style lang="scss" scoped>
.search-bar{
  width: var(--viewport-width);
  margin: 0 auto;
  display: flex;
  padding: 40px 0 40px 0;
  height: 220px;
  z-index: 99;
  position: relative;
  .logo{
    min-width: 328px;
    img{
      width: 328px;
      height: 108px;
      object-fit: contain;
    }
  }
  .search-input-box{
    flex: 1;
    padding-left: 60px;
    position: relative;
    .input{
      height: 76px;
      border-radius: 10px;
      border: 4px solid var(--pc-default-color);
      padding: 10px 30px;
      @include center();
      overflow: hidden;
      position: relative;
      .search-button{
        position: absolute;
        cursor: pointer;
        right: 0;
        top: 0;
        bottom: 0;
        background-color: var(--pc-default-color);
        color: #fff;
        display: inline-block;
        width: 120px;
        @include center();
        img{
          width: 48px;
          height: 48px;
        }
        font-size: 34px;
        height: 100%;
      }
    }
    .input:deep(.van-cell) {
      padding: 0 !important;
    }
    .search-hots{
      max-width: 1400px;
      font-size: 26px;
      padding-top: 15px;
      overflow: hidden;
      height: 60px;
      line-height: 50px;
      .hot-label{
        // color: var(--pc-default-color);
        margin-right: 20px;
      }
      .hot-key{
        margin-right: 30px;
        cursor: pointer;
        transition: color .1s;
        white-space: nowrap;
        display: inline-block;
        &:hover{
          color: var(--pc-default-color);
        }
      }
    }
  }
  .opt-btn{
    cursor: pointer;
    @include center();
    transition: color .2s;
    margin-left: 60px;
    height: 80px;
    .label{
      padding: 0 15px;
      display: inline-block;
      margin-top: 20px;
    }
    i{margin-top: 15px;}
    .jt{
      transform: rotateZ(90deg);
    }
    &:hover{
      color: var(--pc-default-color);
    }
    .opt-btn-content{
      display: flex;
      align-items: center;
    }
    .opt-btn-icon{
      width: 64px;
    }
  }
}
.shopping-title{
  padding: 30px;
  border-bottom: 1px solid #efefef;
  font-size: 30px;
}
.cart-list-body {
  width: 800px;
}
.shopping-list{
  max-height: 520px;
  overflow: auto;
  padding: 0 30px;
  .cart-item{
    display: flex;
    cursor: pointer;
    font-size: 32px;
    margin-top: 20px;
    padding: 20px 0;
    transition: color .15s;
    width: 100%;
    .product-image{
      width: 80px;
      height: 80px;
      object-fit: contain;
    }
    .product-title{
      flex: 1;
      padding: 0 40px;
      @include ellipsisMultiline(2);
      line-height: 40px;
    }
    .product-price {
      font-weight: 600;
      padding-right: 10px;
      color: var(--pc-default-color);
    }

    &:hover{
      color: var(--pc-default-color);
    }
  }
}
.my-list-body{
  padding-top: 20px;
  // width: 300px;
}

.my-opt-item{
  display: flex;
  align-items: center;
  font-size: 32px;
  padding: 10px 30px;
  transition: all .15s;
  white-space: nowrap;
  cursor: pointer;
  &:hover{
    color: var(--pc-default-color);
  }
  .my-opt-label{
    margin-left: 20px;
  }
  &:last-child{
    margin-bottom: 20px;
    border-bottom: 2px solid #cacaca;
  }
}
.view-cart-btn{
  margin: 30px;
  width: calc(100% - 60px);
}
.no-data{
  color: #999;
  font-size: 28px;
  text-align: center;
  padding: 30px 0;
}
.associative-words{
  position: absolute;
  left: 60px;
  right: 180px;
  top: 80px;
  z-index: 9;
  background-color: #fff;
  max-height: 1000px;
  overflow-y: auto;
  box-shadow: 0 0 4px #cacaca;
  border: 1px solid #efefef;
  border-radius: 12px;

  &::-webkit-scrollbar{
    display: none;
  }
  &:hover{
    &::-webkit-scrollbar{
      display: block;
    }
  }
  @include pc__scrollbar('', 12px);
  
  .keyword{
    padding: 25px 30px;
    cursor: pointer;
    &:hover{
      background-color: #efefef;
      color: var(--pc-default-color);
    }
  }
}
</style>
